import { forwardRef, useEffect, useRef } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'

import { cn } from '../../lib/cn'
import { Icon } from './Icon'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode
  hint?: ReactNode
  /** Partial selection — a group where some but not all rows are checked. */
  indeterminate?: boolean
  tone?: 'paper' | 'chrome'
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, indeterminate = false, tone = 'paper', className, disabled, ...rest },
  ref,
) {
  const innerRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate
  }, [indeterminate])

  return (
    <label
      className={cn(
        'group flex items-start gap-2.5',
        disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer',
        className,
      )}
    >
      <span className="relative flex h-[18px] w-[18px] shrink-0 items-center justify-center">
        <input
          ref={(node) => {
            innerRef.current = node
            if (typeof ref === 'function') ref(node)
            else if (ref) ref.current = node
          }}
          type="checkbox"
          disabled={disabled}
          className={cn(
            'ring-focus peer h-[18px] w-[18px] cursor-[inherit] appearance-none rounded-sm border transition-colors',
            tone === 'chrome'
              ? 'border-chrome-600 bg-chrome-800 checked:border-brass checked:bg-brass indeterminate:border-brass indeterminate:bg-brass'
              : 'border-hair bg-surface checked:border-brass checked:bg-brass indeterminate:border-brass indeterminate:bg-brass',
          )}
          {...rest}
        />
        <Icon
          name={indeterminate ? 'minus' : 'check'}
          size={13}
          className="pointer-events-none absolute text-chrome-900 opacity-0 peer-checked:opacity-100 peer-indeterminate:opacity-100"
        />
      </span>
      {label || hint ? (
        <span className="flex flex-col gap-0.5">
          {label ? (
            <span
              className={cn(
                't-small',
                tone === 'chrome' ? 'text-chrome-ink' : 'text-ink',
              )}
            >
              {label}
            </span>
          ) : null}
          {hint ? (
            // Same fix as Field.tsx's hint: ink-faint fails AA for read text, ink-mute doesn't.
            <span
              className={cn(
                't-small',
                tone === 'chrome' ? 'text-chrome-mute' : 'text-ink-mute',
              )}
            >
              {hint}
            </span>
          ) : null}
        </span>
      ) : null}
    </label>
  )
})
